import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bread } from "@/components/Bread";
import { CompletionScreen } from "@/components/CompletionScreen";
import { DayLoader } from "@/components/DayLoader";
import { ProgressBar } from "@/components/ProgressBar";
import { Pronounce } from "@/components/Pronounce";
import { useSettings } from "@/hooks/useSettings";
import { pad, pad3 } from "@/lib/format";
import { gradeFromCorrect, review, studyQueue } from "@/lib/study";
import type { DayData, Word } from "@/types/lex";

type FieldState = "idle" | "correct" | "wrong";

interface SentenceParts {
  before: string;
  target: string;
  after: string;
}

export function FillBlank() {
  const { day: dayParam } = useParams<{ day: string }>();
  const day = Number(dayParam) || 1;
  return (
    <DayLoader day={day}>
      {(data) => <Body day={day} data={data} />}
    </DayLoader>
  );
}

function Body({ day, data }: { day: number; data: DayData }) {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [queue, setQueue] = useState<readonly Word[]>(() =>
    buildQueue(day, data.words, settings.roundSize)
  );
  const [i, setI] = useState(0);
  const [val, setVal] = useState("");
  const [state, setState] = useState<FieldState>("idle");
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [done, setDone] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const w = queue[i];

  useEffect(() => {
    inputRef.current?.focus();
  }, [i]);

  const parts = useMemo<SentenceParts | null>(() => (w ? splitOnTarget(w) : null), [w]);

  function check() {
    if (!parts || !w) return;
    const right = val.trim().toLowerCase() === parts.target.toLowerCase();
    review(day, w.index, gradeFromCorrect(right));
    if (right) {
      setState("correct");
      setCorrect((c) => c + 1);
      if (settings.autoNext) setTimeout(next, 1100);
    } else {
      setState("wrong");
      setWrong((x) => x + 1);
    }
  }

  function next() {
    setVal("");
    setState("idle");
    if (i + 1 >= queue.length) setDone(true);
    else setI(i + 1);
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    // Pressing Enter after the answer has been judged moves forward — pressing
    // it again on an already-correct entry must not double-count the score.
    if (state === "idle") check();
    else next();
  }

  function restart() {
    setQueue(buildQueue(day, data.words, settings.roundSize));
    setI(0);
    setVal("");
    setState("idle");
    setCorrect(0);
    setWrong(0);
    setDone(false);
  }

  const inputWidth = Math.max((parts?.target.length ?? 6) * 14, 100);

  if (queue.length === 0) {
    return (
      <div className="page">
        <div className="container" style={{ paddingTop: "var(--s-7)" }}>
          <div className="empty">No usable example sentences on this day yet.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container" style={{ paddingTop: "var(--s-5)" }}>
        <Bread
          crumbs={[
            { label: "Archive", to: "/" },
            { label: `Day ${pad(day)}`, to: `/day/${day}` },
            { label: "Cloze" },
          ]}
        />
      </div>

      <section className="container" style={{ paddingTop: "var(--s-5)" }}>
        <div className="row-s" style={{ marginBottom: "var(--s-5)" }}>
          <div className="stack" style={{ gap: 4 }}>
            <div className="t-eyebrow">Mode M04 · Cloze</div>
            <h1 className="t-display" style={{ fontSize: "var(--t-2xl)", margin: 0 }}>
              Fill the <span className="italic">missing word</span> in context.
            </h1>
          </div>
          <div className="row-c" style={{ gap: 16 }}>
            <ScorePill dot="moss" value={correct} />
            <ScorePill dot="red" value={wrong} />
            <button className="btn btn-sm" onClick={() => navigate(`/day/${day}`)} type="button">Exit</button>
          </div>
        </div>
        <div className="row-c" style={{ gap: 12, marginBottom: "var(--s-6)" }}>
          <div style={{ flex: 1 }}>
            <ProgressBar value={done ? queue.length : i} max={queue.length} />
          </div>
          <div className="t-mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {pad(i + 1)} / {pad(queue.length)}
          </div>
        </div>

        {!done && w && parts && (
          <div className="cloze-frame">
            <div className="cloze-frame__head">
              <div className="t-mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                ENTRY {pad3(w.index)} · {w.pos.toUpperCase()}
              </div>
              <div className="t-mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                Q · {pad(i + 1)}
              </div>
            </div>

            <div className="cloze-cue">Fill in the blank</div>

            <div className="cloze-sentence">
              <span>{parts.before}</span>
              <input
                ref={inputRef}
                type="text"
                value={val}
                onChange={(e) => {
                  setVal(e.target.value);
                  if (state !== "idle") setState("idle");
                }}
                onKeyDown={onKey}
                className={`cloze-input ${state === "correct" ? "is-correct" : ""} ${
                  state === "wrong" ? "is-wrong" : ""
                }`}
                style={{ width: inputWidth }}
                placeholder={"_".repeat(parts.target.length)}
                spellCheck={false}
                autoComplete="off"
                aria-label="Fill in the blank"
              />
              <span>{parts.after}</span>
            </div>

            <div className="cloze-hints">
              <div className="cloze-hint">
                <span className="t-eyebrow">Meaning</span>
                <span className="t-display italic">{w.meaning_vi}</span>
              </div>
              <div className="cloze-hint">
                <span className="t-eyebrow">Sound</span>
                <span className="row-c" style={{ gap: 8 }}>
                  <Pronounce word={w.word} size={28} />
                  <span className="t-mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>/{w.ipa}/</span>
                </span>
              </div>
              <div className="cloze-hint">
                <span className="t-eyebrow">Starts with</span>
                <span className="t-display" style={{ letterSpacing: "0.04em" }}>
                  {parts.target.slice(0, 1).toUpperCase()}
                  <span style={{ color: "var(--text-faint)" }}>
                    {` · ${parts.target.length} letters`}
                  </span>
                </span>
              </div>
            </div>

            {state === "correct" && (
              <div className="cloze-feedback cloze-feedback--ok">
                <div
                  className="t-mono"
                  style={{ fontSize: 11, color: "var(--accent-moss)", marginBottom: 4 }}
                >
                  CORRECT
                </div>
                <p className="t-display" style={{ fontSize: "var(--t-md)", margin: 0, color: "var(--text)" }}>
                  {parts.before}
                  <strong
                    style={{
                      background: "color-mix(in oklch, var(--accent-moss) 22%, transparent)",
                      padding: "1px 4px",
                      fontWeight: 500,
                    }}
                  >
                    {parts.target}
                  </strong>
                  {parts.after}
                </p>
                <p
                  className="t-display italic"
                  style={{ fontSize: "var(--t-sm)", margin: "6px 0 0 0", color: "var(--text-muted)" }}
                >
                  {w.example_vi}
                </p>
                {!settings.autoNext && (
                  <div className="row-c" style={{ gap: 12, marginTop: 12 }}>
                    <button className="btn btn-sm btn-solid" onClick={next} type="button">Next →</button>
                  </div>
                )}
              </div>
            )}

            {state === "wrong" && (
              <div className="cloze-feedback cloze-feedback--err">
                <div
                  className="t-mono"
                  style={{ fontSize: 11, color: "var(--accent-red)", marginBottom: 4 }}
                >
                  NOT QUITE
                </div>
                <p className="t-display" style={{ fontSize: "var(--t-md)", margin: 0, color: "var(--text)" }}>
                  Answer:{" "}
                  <strong style={{ fontWeight: 500, borderBottom: "1.5px solid var(--ink-0)" }}>
                    {parts.target}
                  </strong>
                </p>
                <p
                  className="t-display italic"
                  style={{ fontSize: "var(--t-sm)", margin: "6px 0 0 0", color: "var(--text-muted)" }}
                >
                  {w.example_vi}
                </p>
                <div className="row-c" style={{ gap: 12, marginTop: 12 }}>
                  <button className="btn btn-sm btn-solid" onClick={next} type="button">Next →</button>
                </div>
              </div>
            )}
          </div>
        )}

        {done && (
          <CompletionScreen
            title={
              <>
                <span className="t-num">{correct}</span>
                <span style={{ color: "var(--text-muted)" }}> / </span>
                {queue.length}
              </>
            }
            subtitle={`Accuracy: ${Math.round((correct / queue.length) * 100)}%`}
            stats={[
              { label: "Correct", value: correct, dot: "moss" },
              { label: "Wrong", value: wrong, dot: "red" },
            ]}
            primaryAction={{ label: "Back to day →", onClick: () => navigate(`/day/${day}`) }}
            secondaryAction={{ label: "↻ Try again", onClick: restart }}
          />
        )}
      </section>
    </div>
  );
}

/**
 * Pick words whose example sentence actually contains the target word (or a
 * basic morphological variant). Otherwise the cloze frame has nothing
 * sensible to mask.
 */
function buildQueue(day: number, words: readonly Word[], size: number): readonly Word[] {
  const usable = words.filter((w) => {
    if (!w.example_en) return false;
    const rx = new RegExp(`\\b${escapeRegex(w.word)}[a-z]{0,4}\\b`, "i");
    return rx.test(w.example_en);
  });
  // Weakest first, same ordering the other drills use.
  return studyQueue(day, usable, size);
}

function splitOnTarget(w: Word): SentenceParts {
  const rx = new RegExp(`\\b(${escapeRegex(w.word)}[a-z]{0,4})\\b`, "i");
  const match = w.example_en.match(rx);
  if (!match || match.index == null) {
    return { before: w.example_en, target: w.word, after: "" };
  }
  return {
    before: w.example_en.slice(0, match.index),
    target: match[0],
    after: w.example_en.slice(match.index + match[0].length),
  };
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function ScorePill({ dot, value }: { dot: "moss" | "red"; value: number }) {
  return (
    <span
      className="t-mono"
      style={{ fontSize: 12, color: dot === "moss" ? "var(--accent-moss)" : "var(--accent-red)" }}
    >
      <span className={`dot dot--${dot}`} style={{ marginRight: 6 }} />
      {value}
    </span>
  );
}
